import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { t } from '@/i18n'
import { cn } from '@/lib/utils'

const DOTS = '...' as const
type PageItem = number | typeof DOTS

function range(start: number, end: number): number[] {
  const length = Math.max(0, end - start + 1)
  return Array.from({ length }, (_, i) => start + i)
}

function getPageRange(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): PageItem[] {
  // first + last + current + 2*siblings + 2*dots
  const totalShown = siblingCount * 2 + 5

  if (totalPages <= totalShown) {
    return range(1, totalPages)
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1)
  const rightSibling = Math.min(currentPage + siblingCount, totalPages)

  const showLeftDots = leftSibling > 2
  const showRightDots = rightSibling < totalPages - 1

  if (!showLeftDots && showRightDots) {
    const leftLen = 3 + 2 * siblingCount
    return [...range(1, leftLen), DOTS, totalPages]
  }

  if (showLeftDots && !showRightDots) {
    const rightLen = 3 + 2 * siblingCount
    return [1, DOTS, ...range(totalPages - rightLen + 1, totalPages)]
  }

  return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, totalPages]
}

function useIsCompact() {
  const [compact, setCompact] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  )

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 639px)')
    const onChange = (e: MediaQueryListEvent) => setCompact(e.matches)
    setCompact(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return compact
}

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Pages on each side of current. Mobile auto-collapses to 0 unless overridden. */
  siblingCount?: number
  className?: string
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount,
  className,
}: PaginationProps) {
  const isCompact = useIsCompact()

  if (totalPages <= 1) return null

  const effectiveSiblings = siblingCount ?? (isCompact ? 0 : 1)
  const pages = getPageRange(currentPage, totalPages, effectiveSiblings)

  return (
    <nav
      className={cn(
        'flex flex-wrap items-center justify-center gap-1 sm:gap-1.5',
        className
      )}
      aria-label="Pagination"
    >
      <Button
        variant="outline"
        size="sm"
        className="h-9 sm:h-10 px-2 sm:px-3"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        aria-label={t('common.previous')}
      >
        <ChevronLeft className="h-4 w-4 sm:mr-1" />
        <span className="hidden sm:inline">{t('common.previous')}</span>
      </Button>

      {pages.map((p, i) =>
        p === DOTS ? (
          <span
            key={i < pages.length / 2 ? 'left-dots' : 'right-dots'}
            className="px-1 sm:px-2 text-muted-foreground select-none"
            aria-hidden="true"
          >
            …
          </span>
        ) : (
          <Button
            key={p}
            variant={p === currentPage ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-9 sm:h-10 min-w-9 sm:min-w-10 px-2 sm:px-3',
              p === currentPage &&
                'bg-primary text-primary-foreground hover:bg-primary/90 pointer-events-none'
            )}
            aria-current={p === currentPage ? 'page' : undefined}
            aria-label={`${t('common.page')} ${p}`}
            onClick={() => onPageChange(p)}
          >
            {p}
          </Button>
        )
      )}

      <Button
        variant="outline"
        size="sm"
        className="h-9 sm:h-10 px-2 sm:px-3"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        aria-label={t('common.next')}
      >
        <span className="hidden sm:inline">{t('common.next')}</span>
        <ChevronRight className="h-4 w-4 sm:ml-1" />
      </Button>
    </nav>
  )
}
